import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useMutation } from 'react-query'
import { ImageQueryGeneration } from 'types'

type ImageQueryGenerationAttributes = {
  prompt: {
    topic: string
    target?: 'stock_provider' | 'templates_library'
  }
}

type Data = ImageQueryGeneration

export interface RequestOptions {
  signal?: AbortSignal
}

interface Variables extends RequestOptions, ImageQueryGenerationAttributes {}

export const createImageQueryGeneration = async (
  workspaceId: number,
  attributes: ImageQueryGenerationAttributes,
): Promise<ImageQueryGeneration> => {
  const res = await sendToApi(`/w/${workspaceId}/image_query_generations`, {
    method: 'POST',
    body: attributes,
  })

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }

  return res.data.image_query_generation
}

export const useCreateImageQueryGenerationSync = () => {
  const workspaceId = useCurrentWorkspaceId()
  return useMutation<Data, Error, Variables>(async ({ signal, ...variables }) => {
    let generation = await createImageQueryGeneration(workspaceId, variables)
    let interval = 500

    do {
      const res = await sendToApi(`/w/${workspaceId}/image_query_generations/${generation.id}`)

      if (res.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error()
      }

      if (signal?.aborted) {
        throw new Error('aborted')
      }

      generation = res.data.image_query_generation

      await new Promise((resolve) => {
        setTimeout(resolve, interval)
      })
    } while (!['failed', 'successful', 'aborted'].includes(generation.status))

    if (!generation.result) {
      throw new Error()
    }

    return generation
  })
}
